import {
  Block,
  Campaign,
  CheckCircle,
  Group,
  LoginOutlined,
  Notifications,
  Search,
  Storefront,
} from "@mui/icons-material";
import {
  Box,
  Fade,
  LinearProgress,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import { useCallback, useEffect, useRef, useState } from "react";
import { Bar, Doughnut } from "react-chartjs-2";
import styled, { keyframes } from "styled-components";
import { useToastStore } from "../../../stores";
import { colorPrimary } from "../../../theme";
import { fetchUsuarioAnalitico } from "../repository/AdminRepository";
import { UsuarioAnaliticoData } from "../types/types";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Filler, Tooltip, Legend);

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const AnimatedBox = styled.div<{ $delay?: number }>`
  animation: ${slideUp} 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: ${({ $delay }) => $delay ?? 0}ms;
  opacity: 0;
`;

const CHART_PALETTE = [
  colorPrimary, "#10b981", "#0284c7", "#8b5cf6", "#ef4444",
  "#f59e0b", "#ec4899", "#14b8a6", "#6366f1", "#78716c",
  "#d946ef", "#06b6d4", "#84cc16", "#f97316", "#a855f7",
  "#22d3ee", "#fb923c", "#a3e635", "#e879f9", "#34d399",
];

function formatNumber(val: number | null): string {
  if (val == null) return "0";
  return val.toLocaleString("pt-BR");
}

interface KpiCardProps {
  label: string;
  value: string;
  subtitle: string;
  icon: React.ReactNode;
  accentColor: string;
  bgColor: string;
  delay: number;
}

function KpiCard({ label, value, subtitle, icon, accentColor, bgColor, delay }: KpiCardProps) {
  return (
    <AnimatedBox $delay={delay}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          position: "relative",
          overflow: "hidden",
          border: "1px solid #f1f5f9",
          height: "100%",
          transition: "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 12px 40px rgba(0,0,0,0.08)",
            borderColor: accentColor + "40",
          },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Typography
            fontSize="0.7rem"
            fontWeight={700}
            textTransform="uppercase"
            letterSpacing={1.5}
            color="#94a3b8"
          >
            {label}
          </Typography>
          <Box
            sx={{
              bgcolor: bgColor,
              color: accentColor,
              p: 1,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {icon}
          </Box>
        </Box>
        <Box display="flex" alignItems="baseline" gap={1}>
          <Typography
            fontSize="2rem"
            fontWeight={900}
            letterSpacing={-1.5}
            color={accentColor}
            lineHeight={1}
          >
            {value}
          </Typography>
          <Typography fontSize="0.7rem" fontWeight={600} color={accentColor + "99"}>
            {subtitle}
          </Typography>
        </Box>
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: 3,
            bgcolor: accentColor + "30",
          }}
        />
      </Paper>
    </AnimatedBox>
  );
}

function KpiCardSkeleton({ delay }: { delay: number }) {
  return (
    <AnimatedBox $delay={delay}>
      <Paper
        elevation={0}
        sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9", overflow: "hidden", position: "relative" }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Skeleton variant="text" width={100} height={16} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: 2 }} />
        </Box>
        <Box display="flex" alignItems="baseline" gap={1}>
          <Skeleton variant="text" width={80} height={40} sx={{ borderRadius: 1 }} />
          <Skeleton variant="text" width={60} height={14} sx={{ borderRadius: 1 }} />
        </Box>
        <Box sx={{ position: "absolute", bottom: 0, left: 0, width: "100%", height: 3, bgcolor: "#e2e8f0" }} />
      </Paper>
    </AnimatedBox>
  );
}

function ChartSkeleton() {
  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9" }}>
      <Skeleton variant="text" width={200} height={24} sx={{ mb: 2, borderRadius: 1 }} />
      <Skeleton variant="rounded" width="100%" height={280} sx={{ borderRadius: 2 }} />
    </Paper>
  );
}

function TableSkeleton() {
  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9" }}>
      <Skeleton variant="text" width={180} height={24} sx={{ mb: 2, borderRadius: 1 }} />
      {Array.from({ length: 5 }).map((_, i) => (
        <Box key={i} display="flex" alignItems="center" gap={2} py={1.5}>
          <Skeleton variant="text" width="60%" height={18} sx={{ borderRadius: 1 }} />
          <Skeleton variant="text" width={60} height={18} sx={{ borderRadius: 1, ml: "auto" }} />
        </Box>
      ))}
    </Paper>
  );
}

const thSx = {
  fontSize: "0.68rem",
  fontWeight: 700,
  textTransform: "uppercase" as const,
  letterSpacing: 1.5,
  color: "#94a3b8",
  borderBottom: "2px solid #f1f5f9",
  py: 1.5,
  whiteSpace: "nowrap" as const,
};

const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "65%",
  plugins: {
    legend: {
      position: "bottom" as const,
      labels: {
        usePointStyle: true,
        padding: 16,
        font: { size: 11, weight: 600 as const },
        color: "#475569",
      },
    },
    tooltip: {
      backgroundColor: "#1e293b",
      titleFont: { size: 13, weight: 700 as const },
      bodyFont: { size: 12 },
      padding: 12,
      cornerRadius: 8,
      callbacks: {
        label: (ctx: any) => {
          const total = ctx.dataset.data.reduce((a: number, b: number) => a + b, 0);
          const pct = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : "0";
          return ` ${ctx.label}: ${ctx.parsed} (${pct}%)`;
        },
      },
    },
  },
};

export function UsuarioAnaliticoTab() {
  const [dashboard, setDashboard] = useState<UsuarioAnaliticoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const { setToast } = useToastStore();
  const jaCarregouRef = useRef(false);

  const fetchDash = useCallback(async () => {
    try {
      setLoading(true);
      const result = await fetchUsuarioAnalitico();
      if (!result?.success) {
        setToast({ variant: "error", message: result.message });
        return;
      }
      setDashboard(result.data);
    } catch {
      setToast({ variant: "error", message: "Erro. Contate o administrador." });
    } finally {
      setLoading(false);
    }
  }, [setToast]);

  useEffect(() => {
    if (jaCarregouRef.current) return;
    jaCarregouRef.current = true;
    fetchDash();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const gruposFull = dashboard?.usuarios_por_grupo ?? [];
  const gruposFiltrados = search
    ? gruposFull.filter((g) => g.grupo_label.toLowerCase().includes(search.toLowerCase()))
    : gruposFull;

  const totalGrupo = gruposFull.reduce((sum, g) => sum + g.qtd_usuario_grupo, 0) || 1;

  const ativoInativoData = {
    labels: ["Ativos", "Inativos"],
    datasets: [{
      data: [dashboard?.qtd_usuarios_ativos ?? 0, dashboard?.qtd_usuarios_inativos ?? 0],
      backgroundColor: ["#10b981", "#ef4444"],
      hoverBackgroundColor: ["#10b981cc", "#ef4444cc"],
      borderWidth: 0,
    }],
  };

  const tipoUsuarioData = {
    labels: ["Lojistas", "Anunciantes"],
    datasets: [{
      data: [dashboard?.qtd_lojistas ?? 0, dashboard?.qtd_anunciantes ?? 0],
      backgroundColor: ["#0284c7", "#8b5cf6"],
      hoverBackgroundColor: ["#0284c7cc", "#8b5cf6cc"],
      borderWidth: 0,
    }],
  };

  const topGruposChart = gruposFull.slice(0, 20);
  const gruposBarData = {
    labels: topGruposChart.map((g) => g.grupo_label),
    datasets: [{
      label: "Qtd Usuários",
      data: topGruposChart.map((g) => g.qtd_usuario_grupo),
      backgroundColor: CHART_PALETTE.slice(0, topGruposChart.length).map((c) => c + "cc"),
      hoverBackgroundColor: CHART_PALETTE.slice(0, topGruposChart.length),
      borderRadius: 6,
      maxBarThickness: 32,
    }],
  };

  const gruposBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y" as const,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#1e293b",
        titleFont: { size: 13, weight: 700 as const },
        bodyFont: { size: 12 },
        padding: 12,
        cornerRadius: 8,
      },
    },
    scales: {
      y: {
        grid: { display: false },
        ticks: { font: { size: 11, weight: 600 as const }, color: "#475569" },
      },
      x: {
        position: "top" as const,
        grid: { color: "#f1f5f9" },
        ticks: { font: { size: 10 }, color: "#94a3b8" },
      },
    },
  };

  const gruposBarHeight = Math.max(320, topGruposChart.length * 40);

  return (
    <Fade in timeout={400}>
      <Box display="flex" flexDirection="column" gap={3}>
        {/* KPI ROW 1 */}
        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" }}
          gap={2.5}
        >
          {loading ? (
            <>
              <KpiCardSkeleton delay={80} />
              <KpiCardSkeleton delay={160} />
              <KpiCardSkeleton delay={240} />
              <KpiCardSkeleton delay={320} />
            </>
          ) : (
            <>
              <KpiCard
                label="Total Usuários"
                value={formatNumber(dashboard?.qtd_usuarios ?? null)}
                subtitle="cadastrados"
                icon={<Group />}
                accentColor="#0284c7"
                bgColor="#e0f2fe"
                delay={80}
              />
              <KpiCard
                label="Ativos"
                value={formatNumber(dashboard?.qtd_usuarios_ativos ?? null)}
                subtitle="usuários"
                icon={<CheckCircle />}
                accentColor="#10b981"
                bgColor="#ecfdf5"
                delay={160}
              />
              <KpiCard
                label="Inativos"
                value={formatNumber(dashboard?.qtd_usuarios_inativos ?? null)}
                subtitle="usuários"
                icon={<Block />}
                accentColor="#ef4444"
                bgColor="#fef2f2"
                delay={240}
              />
              <KpiCard
                label="Sem Logar"
                value={formatNumber(dashboard?.qtd_usuario_sem_logar ?? null)}
                subtitle="usuários"
                icon={<LoginOutlined />}
                accentColor="#64748b"
                bgColor="#f1f5f9"
                delay={320}
              />
            </>
          )}
        </Box>

        {/* KPI ROW 2 */}
        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr 1fr" }}
          gap={2.5}
        >
          {loading ? (
            <>
              <KpiCardSkeleton delay={380} />
              <KpiCardSkeleton delay={420} />
              <KpiCardSkeleton delay={460} />
            </>
          ) : (
            <>
              <KpiCard
                label="Lojistas"
                value={formatNumber(dashboard?.qtd_lojistas ?? null)}
                subtitle="usuários"
                icon={<Storefront />}
                accentColor="#8b5cf6"
                bgColor="#f5f3ff"
                delay={380}
              />
              <KpiCard
                label="Anunciantes"
                value={formatNumber(dashboard?.qtd_anunciantes ?? null)}
                subtitle="usuários"
                icon={<Campaign />}
                accentColor="#f59e0b"
                bgColor="#fffbeb"
                delay={420}
              />
              <KpiCard
                label="Notificações Ativas"
                value={formatNumber(dashboard?.qtd_notification_on ?? null)}
                subtitle="usuários"
                icon={<Notifications />}
                accentColor="#14b8a6"
                bgColor="#f0fdfa"
                delay={460}
              />
            </>
          )}
        </Box>

        {/* DOUGHNUTS: Ativos vs Inativos | Lojistas vs Anunciantes */}
        <Box display="grid" gridTemplateColumns={{ xs: "1fr", lg: "1fr 1fr" }} gap={3}>
          <AnimatedBox $delay={500}>
            {loading ? (
              <ChartSkeleton />
            ) : (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9", height: "100%" }}>
                <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                  Ativos vs Inativos
                </Typography>
                <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                  Distribuição de usuários por status
                </Typography>
                <Box height={320} display="flex" alignItems="center" justifyContent="center">
                  <Doughnut data={ativoInativoData} options={doughnutOptions} />
                </Box>
              </Paper>
            )}
          </AnimatedBox>

          <AnimatedBox $delay={580}>
            {loading ? (
              <ChartSkeleton />
            ) : (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9", height: "100%" }}>
                <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                  Lojistas vs Anunciantes
                </Typography>
                <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                  Distribuição por tipo de usuário
                </Typography>
                <Box height={320} display="flex" alignItems="center" justifyContent="center">
                  <Doughnut data={tipoUsuarioData} options={doughnutOptions} />
                </Box>
              </Paper>
            )}
          </AnimatedBox>
        </Box>

        {/* TOP GRUPOS BAR CHART */}
        <AnimatedBox $delay={660}>
          {loading ? (
            <ChartSkeleton />
          ) : (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9" }}>
              <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                Top 20 Grupos por Usuários
              </Typography>
              <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                Quantidade de usuários por grupo/concessionária
              </Typography>
              <Box height={gruposBarHeight}>
                <Bar data={gruposBarData} options={gruposBarOptions} />
              </Box>
            </Paper>
          )}
        </AnimatedBox>

        {/* TABELA: Usuários por Grupo */}
        <AnimatedBox $delay={740}>
          {loading ? (
            <TableSkeleton />
          ) : (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
                <Box>
                  <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                    Usuários por Grupo
                  </Typography>
                  <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500}>
                    Todos os grupos com usuários cadastrados
                  </Typography>
                </Box>
                <TextField
                  size="small"
                  placeholder="Buscar grupo..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{ startAdornment: <Search sx={{ fontSize: 18, color: "#94a3b8", mr: 0.5 }} /> }}
                  sx={{
                    minWidth: 220,
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      borderRadius: 2,
                      fontSize: "0.85rem",
                      "& fieldset": { border: "1px solid #e2e8f0" },
                      "&:hover fieldset": { borderColor: colorPrimary + "80" },
                      "&.Mui-focused fieldset": { borderColor: colorPrimary },
                    },
                  }}
                />
              </Box>
              <TableContainer sx={{ maxHeight: 520 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ "& th": { bgcolor: "#f8fafc" } }}>
                      <TableCell sx={thSx}>Grupo</TableCell>
                      <TableCell sx={{ ...thSx, textAlign: "center" }}>Qtd Usuários</TableCell>
                      <TableCell sx={{ ...thSx, minWidth: 200 }}>Proporção</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {gruposFiltrados.map((row) => (
                      <TableRow key={row.num_id_grupo_concessionaria} sx={{ "&:hover": { bgcolor: "#f8fafc" } }}>
                        <TableCell
                          sx={{
                            py: 2,
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            color: "#1e293b",
                            borderBottom: "1px solid #f8fafc",
                          }}
                        >
                          {row.grupo_label}
                        </TableCell>
                        <TableCell
                          sx={{
                            py: 2,
                            fontSize: "0.85rem",
                            fontWeight: 800,
                            color: colorPrimary,
                            textAlign: "center",
                            borderBottom: "1px solid #f8fafc",
                          }}
                        >
                          {formatNumber(row.qtd_usuario_grupo)}
                        </TableCell>
                        <TableCell sx={{ py: 2, borderBottom: "1px solid #f8fafc" }}>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Box flex={1}>
                              <LinearProgress
                                variant="determinate"
                                value={(row.qtd_usuario_grupo / totalGrupo) * 100}
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  bgcolor: "#f1f5f9",
                                  "& .MuiLinearProgress-bar": {
                                    borderRadius: 4,
                                    bgcolor: "#8b5cf6",
                                  },
                                }}
                              />
                            </Box>
                            <Typography fontSize="0.7rem" color="#94a3b8" fontWeight={600} minWidth={32}>
                              {Math.round((row.qtd_usuario_grupo / totalGrupo) * 100)}%
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </AnimatedBox>
      </Box>
    </Fade>
  );
}
